import { Theme } from '@radix-ui/themes'
import React, { useEffect, useMemo, useState } from 'react'
import { connect, ConnectedProps, useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'

import { SHARED_CHANNELS } from '@disco/constants/FeatureTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  createGroup,
  loadGroup,
  updateGroup,
} from '@disco/state-management/actions/GroupActions'
import { resetForm } from '@disco/state-management/reducers/forms/channel-share'
import { isFeatureEnabled } from '@disco/state-management/selectors/business'
import { getGroupShareSortSettings } from '@disco/state-management/selectors/explorer'
import { getChannelShareForm } from '@disco/state-management/selectors/forms'
import {
  getGroup,
  getIsSubfolder,
  getNestedGroup,
} from '@disco/state-management/selectors/groups'
import {
  getBusinessUsers,
  getPlaylistExclusionList,
  getUser,
} from '@disco/state-management/selectors/user'

import { Badge, Flex } from 'components/ds'
import { Tab, Tabs } from 'components/ui'
import Heading from 'components/ui/heading'
import ModalWindow from 'components/ui/modal-window'
import Tooltip from 'components/ui/tooltip'

import type { RootState } from 'store/configureStore'

import MyTeamTab from './share-tabs/tab-content/MyTeamTab'
import PublicLinkTab from './share-tabs/tab-content/PublicLinkTab'
import { ShareOtherDiscoTab } from './share-tabs/tab-content/ShareOtherDiscoTab'
import type { ChannelShareFormProps } from './types'

type TabItem = {
  label: React.ReactNode
  content: React.ReactNode
  disabled?: boolean
  beta?: string
}

const mapStateToProps = (
  state: RootState,
  { params: { channelId } }: { params: { channelId: string } },
) => {
  const group = getNestedGroup(state, {
    groupId: parseInt(channelId),
  })
  const parentGroup =
    group?.parent &&
    getGroup(state, {
      groupId: group.parent,
    })
  return {
    ...getChannelShareForm(state),
    user: getUser(state),
    businessMembers: getBusinessUsers(state),
    exclusionList: getPlaylistExclusionList(state),
    group,
    parentGroup,
    isSubfolder: getIsSubfolder(state, {
      groupId: parseInt(channelId),
    }),
    sortParams: getGroupShareSortSettings(state, {
      groupId: parseInt(channelId),
    }),
    isSharedChannelsEnabled: isFeatureEnabled(state, {
      featureName: SHARED_CHANNELS,
    }),
  }
}

const ChannelShare = (props: ChannelShareFormProps) => {
  const { group, isSharedChannelsEnabled, isSubfolder } = props

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const modalTitle = useMemo(() => {
    let name = 'Share channel'
    if (isSubfolder) name = 'Share subfolder'
    if (!isSubfolder && group.parent) name = 'Share folder'
    return name
  }, [isSubfolder, group.parent])

  const onClose = () => {
    dispatch(resetForm())
    // Direct back to the channels settings page if the user is within the settings page
    navigate(
      location.pathname.includes('/settings/channels')
        ? '/settings/channels'
        : '/',
    )
  }

  useEffect(() => {
    dispatch(
      sendMixpanelEvent('Open modal', {
        Modal: 'Share Channel',
        'Channel Id': group.id,
      }),
    )
  }, [])

  const showMyTeamTab = group.editable && !group.parent
  const showShareOtherDiscoTab =
    !group.parent &&
    group.business_role == 'owner' &&
    group.editable &&
    isSharedChannelsEnabled
  const showLinkTab = group.business_role !== 'partner'

  const tabs: TabItem[] = []

  tabs.push({
    label: 'My team',
    content: <MyTeamTab {...props} onClose={onClose} />,
    disabled: !showMyTeamTab,
  })

  if (isSharedChannelsEnabled && group.business_role == 'owner')
    tabs.push({
      label: 'Other DISCOs',
      content: <ShareOtherDiscoTab channelId={group.id} />,
      disabled: !showShareOtherDiscoTab,
    })

  tabs.push({
    label: 'Link',
    content: <PublicLinkTab {...props} />,
    disabled: !showLinkTab,
  })

  // Start with the first tab that is not disabled
  const [index, setIndex] = useState(tabs.findIndex(tab => !tab.disabled) || 0)

  return (
    <ModalWindow
      header={<Heading>{modalTitle}</Heading>}
      isOpen
      width="medium"
      overflowVisible
      onClose={onClose}
    >
      <Theme>
        {tabs.length === 1 ? (
          <div>{tabs[0].content}</div>
        ) : (
          <Tabs
            className="Tabs--modal Tabs--channel-share"
            index={index}
            onChange={setIndex}
          >
            {tabs.map((tab, i) => {
              const label =
                tab.disabled || tab.beta ? (
                  <Tooltip
                    key={i}
                    content={
                      tab.disabled
                        ? 'Only channel managers can do this'
                        : tab.beta
                    }
                    position="bottom"
                  >
                    <Flex align="center" gap="2">
                      {tab.label}
                      {tab.beta && (
                        <Badge
                          size="1"
                          variant="solid"
                          color={index === i ? undefined : 'gray'}
                        >
                          BETA
                        </Badge>
                      )}
                    </Flex>
                  </Tooltip>
                ) : (
                  tab.label
                )

              return (
                <Tab key={i} label={label} disabled={tab.disabled}>
                  {tab.content}
                </Tab>
              )
            })}
          </Tabs>
        )}
      </Theme>
    </ModalWindow>
  )
}

const mapDispatchToProps = {
  resetForm,
  loadGroup,
  updateGroup,
  createGroup,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

const ConnectedChannelShare = connector(ChannelShare)

export default function ChannelShareWithRouter(props: Partial<PropsFromRedux>) {
  const params = useParams<{ channelId: string }>()
  if (!params.channelId) return null
  return (
    <ConnectedChannelShare
      {...props}
      params={params as { channelId: string }}
    />
  )
}
